import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Star, Heart, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPECIALTIES = {
  general: { name_fr: 'Médecine générale', name_ar: 'طب عام' },
  cardiology: { name_fr: 'Cardiologie', name_ar: 'أمراض القلب' },
  dermatology: { name_fr: 'Dermatologie', name_ar: 'الأمراض الجلدية' },
  gynecology: { name_fr: 'Gynécologie', name_ar: 'أمراض النساء' },
  ophthalmology: { name_fr: 'Ophtalmologie', name_ar: 'طب العيون' },
  pediatrics: { name_fr: 'Pédiatrie', name_ar: 'طب الأطفال' },
  dentistry: { name_fr: 'Dentiste', name_ar: 'طب الأسنان' },
  orthopedics: { name_fr: 'Orthopédie', name_ar: 'جراحة العظام' },
  psychiatry: { name_fr: 'Psychiatrie', name_ar: 'الطب النفسي' },
  neurology: { name_fr: 'Neurologie', name_ar: 'الأمراض العصبية' },
  urology: { name_fr: 'Urologie', name_ar: 'المسالك البولية' },
  ent: { name_fr: 'ORL', name_ar: 'أنف أذن حنجرة' },
  radiology: { name_fr: 'Radiologie', name_ar: 'الأشعة' },
  gastroenterology: { name_fr: 'Gastro-entérologie', name_ar: 'أمراض الجهاز الهضمي' },
  pneumology: { name_fr: 'Pneumologie', name_ar: 'أمراض الرئة' },
  general_surgery: { name_fr: 'Chirurgie générale', name_ar: 'الجراحة العامة' },
  endocrinology: { name_fr: 'Endocrinologie', name_ar: 'الغدد الصماء' },
  anesthesiology: { name_fr: 'Anesthésiologie', name_ar: 'التخدير' },
  rheumatology: { name_fr: 'Rhumatologie', name_ar: 'أمراض الروماتيزم' },
  physical_medicine: { name_fr: 'Médecine physique', name_ar: 'الطب الطبيعي' },
  nephrology: { name_fr: 'Néphrologie', name_ar: 'أمراض الكلى' },
  neurosurgery: { name_fr: 'Neurochirurgie', name_ar: 'جراحة المخ والأعصاب' },
};

const WILAYAS = {
  '01': 'Adrar', '02': 'Chlef', '03': 'Laghouat', '04': 'Oum El Bouaghi', '05': 'Batna',
  '06': 'Béjaïa', '07': 'Biskra', '08': 'Béchar', '09': 'Blida', '10': 'Bouira',
  '11': 'Tamanrasset', '12': 'Tébessa', '13': 'Tlemcen', '14': 'Tiaret', '15': 'Tizi Ouzou',
  '16': 'Alger', '17': 'Djelfa', '18': 'Jijel', '19': 'Sétif', '20': 'Saïda',
  '21': 'Skikda', '22': 'Sidi Bel Abbès', '23': 'Annaba', '24': 'Guelma', '25': 'Constantine',
  '26': 'Médéa', '27': 'Mostaganem', '28': "M'Sila", '29': 'Mascara', '30': 'Ouargla',
  '31': 'Oran', '32': 'El Bayadh', '33': 'Illizi', '34': 'Bordj Bou Arréridj', '35': 'Boumerdès',
  '36': 'El Tarf', '37': 'Tindouf', '38': 'Tissemsilt', '39': 'El Oued', '40': 'Khenchela',
  '41': 'Souk Ahras', '42': 'Tipaza', '43': 'Mila', '44': 'Aïn Defla', '45': 'Naâma',
  '46': 'Aïn Témouchent', '47': 'Ghardaïa', '48': 'Relizane',
  '49': 'Timimoun', '50': 'Bordj Badji Mokhtar', '51': 'Ouled Djellal', '52': 'Béni Abbès', '53': 'In Salah',
  '54': 'In Guezzam', '55': 'Touggourt', '56': 'Djanet', '57': 'El Meghaier', '58': 'El Menia',
  '59': 'Aflou', '60': 'Ain Oussera', '61': 'Barika', '62': 'Bir El Ater', '63': 'Bou Saada',
  '64': 'El Abiodh Sidi Cheikh', '65': 'El Aricha', '66': 'El Kantara', '67': 'Ksar Chellala', '68': 'Ksar El Boukhari',
  '69': 'Messaad',
};

const DoctorCard = ({ doctor }) => {
  const { isAuthenticated } = useAuth();
  const { t, lang, isRTL } = useLanguage();
  const [isFav, setIsFav] = useState(false);
  const spec = SPECIALTIES[doctor.specialty] || { name_fr: doctor.specialty, name_ar: doctor.specialty };
  // Un établissement (labo, cabinet de groupe...) n'a souvent pas de
  // spécialité renseignée (specialty=None) : sans ce repli, sa carte
  // affichait une ligne vide à la place de "Laboratoire" / "Cabinet".
  const specialtyName = doctor.specialty ? (lang === 'ar' ? spec.name_ar : spec.name_fr) : (doctor.entity_type || '');
  const wilaya = WILAYAS[doctor.wilaya_code] || doctor.wilaya_code;
  const isOnline = doctor.is_registered && doctor.status === 'approved';

  useEffect(() => {
    if (!isAuthenticated) return;
    axios.get(`${API_URL}/api/favorites/check/${doctor.doctor_id}`, { withCredentials: true })
      .then(r => setIsFav(r.data.is_favorite))
      .catch(() => {});
  }, [isAuthenticated, doctor.doctor_id]);

  const toggleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) return;
    try {
      if (isFav) {
        await axios.delete(`${API_URL}/api/favorites/${doctor.doctor_id}`, { withCredentials: true });
        setIsFav(false);
      } else {
        await axios.post(`${API_URL}/api/favorites/${doctor.doctor_id}`, {}, { withCredentials: true });
        setIsFav(true);
      }
    } catch {
      // Correctif : un echec (reseau, session expiree, etc.) etait avale
      // silencieusement — le coeur ne changeait pas d'etat et rien n'indiquait
      // a l'utilisateur que son clic n'avait rien fait.
      toast.error(t('fav_toggle_error'));
    }
  };

  return (
    <div
      className="bg-white rounded-2xl border border-[#E2E8F0] p-4 sm:p-6 hover:shadow-md transition-all duration-300 hover:-translate-y-1 relative min-w-0"
      data-testid={`doctor-card-${doctor.doctor_id}`}
    >
      {isAuthenticated && (
        <button onClick={toggleFav} className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} z-10 p-1.5 rounded-full hover:bg-[#FEE2E2]/50 transition-colors`} data-testid={`fav-btn-${doctor.doctor_id}`}>
          <Heart className={`w-5 h-5 transition-colors ${isFav ? 'fill-red-500 text-red-500' : 'text-[#CBD5E1] hover:text-red-400'}`} />
        </button>
      )}
      <div className="flex gap-4">
        <div className="flex-shrink-0">
          {doctor.photo_url ? (
            <img
              src={doctor.photo_url}
              alt={doctor.name}
              className="w-20 h-20 rounded-xl object-cover"
            />
          ) : (
            <div className="w-20 h-20 rounded-xl bg-[#E6F4FB] flex items-center justify-center">
              <span className="text-2xl font-bold text-[#0596DE]">
                {(doctor.name || doctor.full_name || "").split(' ').map(n => n[0]).join('').slice(0, 2)}
              </span>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              {/* min-w-0 : sans lui, un flex-item refuse de descendre sous la
                  largeur naturelle de son contenu — "truncate" sur le <h3>
                  ci-dessous ne servait donc à rien pour les tres longues
                  raisons sociales (ex. "LABORATOIRE D'ANALYSES MEDICALES
                  BENSER...") qui debordaient de la carte. */}
              <h3 className="font-semibold text-[#0F172A] text-lg truncate">{doctor.name}</h3>
              <p className="text-[#0596DE] font-medium truncate">{specialtyName}</p>
              {doctor.average_rating > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <Star className="w-3.5 h-3.5 fill-[#F59E0B] text-[#F59E0B]" />
                  <span className="text-sm font-medium text-[#0F172A]">{doctor.average_rating}</span>
                  <span className="text-xs text-[#64748B]">({doctor.review_count})</span>
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-1.5">
              {isOnline ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D1FAE5] text-[#10B981] text-sm font-semibold whitespace-nowrap">
                  <span className="w-2 h-2 rounded-full bg-[#10B981] pulse-dot"></span>
                  {t('card_online')}
                </span>
              ) : null}
              {doctor.video_consultation_enabled ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#E0F2FE] text-[#0596DE] text-xs font-semibold whitespace-nowrap">
                  <Video className="w-3 h-3" />
                  {t('card_video_consultation')}
                </span>
              ) : null}
            </div>
          </div>
          <div className="mt-3 space-y-1.5 text-sm text-[#64748B]">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{doctor.address} - {wilaya}</span>
            </div>
            
          </div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-[#E2E8F0] flex gap-3">
        {isOnline ? (
          <>
            <Link to={doctor.slug ? `/medecin/${doctor.slug}` : `/doctor/${doctor.doctor_id}`} className="flex-1">
              <Button
                className="w-full rounded-full bg-[#0596DE] hover:bg-[#047CBA]"
                data-testid={`book-online-btn-${doctor.doctor_id}`}
              >
                {t('card_book')}
              </Button>
            </Link>
            <Link to={doctor.slug ? `/medecin/${doctor.slug}` : `/doctor/${doctor.doctor_id}`}>
              <Button variant="outline" className="rounded-full min-h-[48px] px-4" data-testid={`view-profile-btn-${doctor.doctor_id}`}>
                {t('card_profile')}
              </Button>
            </Link>
          </>
        ) : (
          <>
            <a href={`tel:${doctor.phone}`} className="flex-1">
              <Button
                variant="outline"
                className="w-full rounded-full border-[#0596DE] text-[#0596DE] hover:bg-[#E6F4FB] min-h-[48px]"
                data-testid={`call-btn-${doctor.doctor_id}`}
              >
                <Phone className="w-4 h-4 mr-2" />
                {doctor.phone}
              </Button>
              <p className="text-xs text-[#F59E0B] font-medium mt-1 text-center">🔜 Bientôt disponible en ligne</p>
            </a>
            <Link to={doctor.slug ? `/medecin/${doctor.slug}` : `/doctor/${doctor.doctor_id}`}>
              <Button variant="outline" className="rounded-full min-h-[48px] px-4" data-testid={`view-profile-btn-${doctor.doctor_id}`}>
                {t('card_profile')}
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default DoctorCard;
